import { Platform, View } from 'react-native';
import { FaIcon, Icon } from 'shared/components/icon';
import { Link } from 'shared/components/link';
import { Text } from 'shared/components/text';
import { Props } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';

type InfoGridItem = {
  title: string;
  description?: string;
  icon?: FaIcon;
  comingSoon?: boolean;
  linkTitle?: string;
  linkHref?: string;
};
type InfoGridVariant = 'green' | 'cream';

export function InfoGrid({
  items,
  className,
  large,
  descriptionClassName,
  variant = 'green',
  singleColumn = false,
  twoColumnsOnMobile = false,
  threeColumnsOnDesktop = false,
}: Props<{
  items: InfoGridItem[];
  large?: boolean;
  descriptionClassName?: string;
  variant?: InfoGridVariant;
  singleColumn?: boolean;
  twoColumnsOnMobile?: boolean;
  threeColumnsOnDesktop?: boolean;
}>) {
  const { t } = useTranslation('common');
  const iconContainerClassName =
    variant === 'cream' ? 'bg-brand-cream-dark' : 'bg-brand-green-light';
  const iconClassName = variant === 'cream' ? 'text-brand-brown' : 'text-brand-green-dark';
  const compactItemClassName = 'min-w-0 gap-2';
  const compactItemHeaderClassName = 'min-w-0 flex-row items-center gap-3';
  const isNative = Platform.OS !== 'web';

  if (isNative) {
    const renderIcon = (item: InfoGridItem, alignWithDescription = false) =>
      item.icon ? (
        <View
          className={clsx(`rounded-2xl ${iconContainerClassName}`, {
            'mt-1': alignWithDescription && item.description,
            'p-3.5': large,
            'p-2': !large,
          })}
        >
          <Icon
            icon={item.icon}
            className={clsx(iconClassName, {
              'size-7': large,
              'size-5': !large,
            })}
          />
        </View>
      ) : null;

    const renderTitle = (item: InfoGridItem, compact = false) => (
      <Text
        numberOfLines={compact ? 1 : undefined}
        className={clsx('text-brand-brown-dark', {
          'font-bold': !!item.description,
          'text-lg': large,
        })}
      >
        {item.title}
      </Text>
    );

    const renderDescription = (item: InfoGridItem) =>
      item.description ? (
        <Text
          numberOfLines={twoColumnsOnMobile ? 2 : undefined}
          className={clsx(
            'text-brand-brown-dark/70',
            singleColumn || twoColumnsOnMobile
              ? 'text-base leading-6'
              : large
                ? 'text-base'
                : 'text-sm',
            descriptionClassName,
          )}
        >
          {item.description}
        </Text>
      ) : null;

    const renderComingSoon = (item: InfoGridItem) =>
      item.comingSoon ? (
        <Text className={'text-brand-brown-dark/70 mt-1 text-xs italic'}>({t('coming_soon')})</Text>
      ) : null;

    const renderItem = (item: InfoGridItem, index: number, compact = false) => {
      if (compact) {
        return (
          <View key={index} className={compactItemClassName}>
            <View className={compactItemHeaderClassName}>
              {renderIcon(item)}
              <View className={'min-w-0 flex-1'}>{renderTitle(item, true)}</View>
            </View>
            <View className={'min-w-0'}>
              {renderDescription(item)}
              {renderComingSoon(item)}
            </View>
          </View>
        );
      }

      return (
        <View
          key={index}
          className={clsx('flex-row gap-3', {
            'items-start': item.description,
            'items-center': !item.description,
          })}
        >
          {renderIcon(item, true)}

          <View className={'min-w-0 flex-1'}>
            {renderTitle(item)}
            {renderDescription(item)}
            {renderComingSoon(item)}
          </View>
        </View>
      );
    };

    if (twoColumnsOnMobile) {
      const rows: InfoGridItem[][] = [];

      for (let i = 0; i < items.length; i += 2) {
        rows.push(items.slice(i, i + 2));
      }

      return (
        <View className={clsx(className, large ? 'gap-6' : 'gap-3')}>
          {rows.map((row, rowIndex) => (
            <View key={rowIndex} className={clsx('flex-row', large ? 'gap-6' : 'gap-3')}>
              {row.map((item, itemIndex) => (
                <View key={rowIndex * 2 + itemIndex} className={'min-w-0 flex-1'}>
                  {renderItem(item, rowIndex * 2 + itemIndex, true)}
                </View>
              ))}
              {row.length < 2 && <View className={'min-w-0 flex-1'} />}
            </View>
          ))}
        </View>
      );
    }

    return (
      <View className={clsx(className, singleColumn ? 'gap-4' : large ? 'gap-6' : 'gap-3')}>
        {items.map((item, index) => renderItem(item, index))}
      </View>
    );
  }

  return (
    <View
      className={clsx(
        'flex-row',
        className,
        singleColumn
          ? 'flex-cols-1 md:flex-cols-2 gap-4 md:gap-5'
          : large
            ? 'flex-cols-1 md:flex-cols-3 gap-6 md:gap-12'
            : twoColumnsOnMobile
              ? clsx(
                  'flex-cols-2 gap-3 md:gap-4',
                  threeColumnsOnDesktop ? 'md:flex-cols-3' : 'md:flex-cols-2',
                )
              : 'flex-cols-1 md:flex-cols-2 gap-3 md:gap-6',
      )}
    >
      {items.map((item, index) => {
        const icon = item.icon ? (
          <View
            className={`rounded-2xl ${iconContainerClassName} ${large ? 'p-3.5 md:p-4' : 'p-2'} ${item.description && !twoColumnsOnMobile ? 'mt-1' : ''}`}
          >
            <Icon
              icon={item.icon}
              className={`${iconClassName} ${large ? 'size-7 md:size-8' : 'size-5 md:size-6'}`}
            />
          </View>
        ) : null;

        const title = (
          <Text
            numberOfLines={twoColumnsOnMobile ? 1 : undefined}
            className={clsx('text-brand-brown-dark', {
              'font-bold': !!item.description,
              'text-lg': large,
            })}
          >
            {item.title}
          </Text>
        );

        const description = item.description ? (
          <Text
            numberOfLines={twoColumnsOnMobile ? 2 : undefined}
            className={clsx(
              'text-brand-brown-dark/70',
              singleColumn || twoColumnsOnMobile
                ? 'text-base leading-6'
                : large
                  ? 'text-base'
                  : 'text-sm md:text-base',
              descriptionClassName,
            )}
          >
            {item.description}
          </Text>
        ) : null;

        const comingSoon = item.comingSoon ? (
          <Text className={'text-brand-brown-dark/70 mt-1 text-xs italic md:text-sm'}>
            ({t('coming_soon')})
          </Text>
        ) : null;

        const link =
          item.linkTitle && item.linkHref ? (
            <Link
              href={item.linkHref}
              skipDefaults={true}
              textClassName={
                'text-brand-brown-dark mt-0.5 text-sm font-semibold underline md:text-base'
              }
            >
              {item.linkTitle}
            </Link>
          ) : null;

        if (twoColumnsOnMobile) {
          return (
            <View key={index} className={compactItemClassName}>
              <View className={compactItemHeaderClassName}>
                {icon}
                <View className={'min-w-0 flex-1'}>{title}</View>
              </View>
              <View className={'min-w-0'}>
                {description}
                {comingSoon}
                {link}
              </View>
            </View>
          );
        }

        return (
          <View
            key={index}
            className={clsx('flex-row gap-3', {
              'items-start': item.description,
              'items-center': !item.description,
            })}
          >
            {icon}
            <View className={'flex-1'}>
              {title}
              {description}
              {comingSoon}
              {link}
            </View>
          </View>
        );
      })}
    </View>
  );
}
